import React, { useState, useEffect } from 'react';
import {
  Monitor, Plus, Search, Filter, Edit, Trash2, User,
  MapPin, Building, Calendar, DollarSign, AlertCircle,
  CheckCircle, XCircle, Eye, Download, FileText, UserPlus, UserMinus, X, Upload, Settings,
  History, Clock, Users, Laptop, QrCode, ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  Camera, Copy
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';

const API_BASE_URL = import.meta.env.VITE_API_URL;

interface Computer {
  id: number;
  inventory_number: string;
  brand_id?: number;
  model_id?: number;
  brand: string;
  model: string;
  serial_number: string;
  storage?: string;
  storage_type?: string;
  ram?: string;
  processor?: string;
  graphics_card?: string;
  pro_support_enabled?: boolean;
  pro_support_end_date?: string;
  purchase_date?: string;
  warranty_end_date?: string;
  invoice_url?: string;
  purchase_price?: number;
  currency?: string;
  location_id?: number;
  location_name?: string;
  company_id?: number;
  company_name?: string;
  assigned_user_id?: number;
  assigned_user_name?: string;
  assigned_user_email?: string;
  status: 'active' | 'inactive' | 'under_repair' | 'damaged' | 'retired' | 'scrapped';
  notes?: string;
  brand_name?: string;
  model_name?: string;
  brand_logo?: string;
  model_image?: string;
  created_at: string;
  created_by_name?: string;
}

interface Brand {
  id: number;
  name: string;
  logo_url?: string;
  description?: string;
  is_active: boolean;
}

interface Model {
  id: number;
  brand_id: number;
  name: string;
  image_url?: string;
  processor?: string;
  graphics_card?: string;
  wifi_support?: string;
  battery_info?: string;
  screen_resolution?: string;
  storage_capacity?: string;
  storage_type?: string;
  ram?: string;
  description?: string;
  is_active: boolean;
  brand_name?: string;
}

interface Company {
  id: number;
  name: string;
}

interface Location {
  id: number;
  name: string;
}

interface UserItem {
  id: number;
  name: string;
  email: string;
}

interface ComputerHistory {
  id: number;
  computer_id: number;
  action_type: 'create' | 'update' | 'assign' | 'unassign' | 'delete';
  changed_fields: string | null;
  old_values: string | null;
  new_values: string | null;
  performed_by_user_id: number | null;
  performed_by_user_name: string | null;
  assigned_user_id: number | null;
  assigned_user_name: string | null;
  notes: string | null;
  created_at: string;
}


const ComputersPage: React.FC = () => {
  const navigate = (path: string) => {
    window.dispatchEvent(new CustomEvent('navigate', { detail: path.replace('/', '') }));
  };
  const { t, language } = useLanguage();
  const [computers, setComputers] = useState<Computer[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [editingComputer, setEditingComputer] = useState<Computer | null>(null);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('computers', setSearchTerm);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCompany, setFilterCompany] = useState<string>('all');
  const [selectedComputer, setSelectedComputer] = useState<Computer | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [filteredModels, setFilteredModels] = useState<Model[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Zimmet formu modal states
  const [showAssignmentFormModal, setShowAssignmentFormModal] = useState(false);
  const [assignmentFormNotes, setAssignmentFormNotes] = useState('');
  const [pendingAssignment, setPendingAssignment] = useState<{
    computer: Computer | null;
    userId: string;
    userName: string;
  } | null>(null);

  // Atama kaldırma (Geri zimmet) modal states
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [unassignmentFormNotes, setUnassignmentFormNotes] = useState('');
  const [pendingUnassignment, setPendingUnassignment] = useState<Computer | null>(null);
  const [unassignmentPhotos, setUnassignmentPhotos] = useState<string[]>([]);

  // Location filter ve History modal states
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [computerHistory, setComputerHistory] = useState<ComputerHistory[]>([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Sorting states
  const [sortField, setSortField] = useState<string>('inventory_number');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Age filter states (configurable year tracking)
  const [ageFilter, setAgeFilter] = useState<'all' | 'over5years' | 'near5years'>('all');
  const [selectedComputerForHistory, setSelectedComputerForHistory] = useState<Computer | null>(null);
  const [ageThresholdYears, setAgeThresholdYears] = useState<number>(() => {
    const saved = localStorage.getItem('computers_ageThresholdYears');
    return saved ? parseInt(saved) : 5;
  });
  const [warningMonths, setWarningMonths] = useState<number>(() => {
    const saved = localStorage.getItem('computers_warningMonths');
    return saved ? parseInt(saved) : 3;
  });
  const [showAgeSettings, setShowAgeSettings] = useState(false);
  const [ageSettingsDraft, setAgeSettingsDraft] = useState({ years: 5, months: 3 });

  // Hurda Modal States
  const [showScrapModal, setShowScrapModal] = useState(false);
  const [pendingScrapComputer, setPendingScrapComputer] = useState<Computer | null>(null);
  const [scrapFormStep, setScrapFormStep] = useState(1);
  const [scrapFormData, setScrapFormData] = useState({
    device_type: 'desktop' as 'laptop' | 'desktop' | 'workstation' | 'server',
    hardware_checklist: {
      cpu: { status: 'exists', detail: '', spare_part: false },
      ram: { status: 'exists', detail: '', spare_part: false },
      storage: { status: 'exists', detail: '', spare_part: false },
      screen: { status: 'na', detail: '', spare_part: false },
      motherboard: { status: 'working', detail: '', spare_part: false },
      battery: { status: 'na', detail: '', spare_part: false },
      adapter: { status: 'exists', detail: '', spare_part: false },
      case_condition: { status: 'good', detail: '', spare_part: false },
      keyboard: { status: 'working', detail: '', spare_part: false },
      ports: { status: 'good', detail: '', spare_part: false }
    },
    scrap_reasons: [] as string[],
    scrap_reason_detail: '',
    disk_removed: false,
    data_wipe_method: 'na' as 'software_wipe' | 'physical_drill' | 'degauss' | 'not_wiped' | 'na',
    media_check_done: false,
    data_security_notes: '',
    photos: [] as File[]
  });
  const [scrapFormLoading, setScrapFormLoading] = useState(false);

  const [computerData, setComputerData] = useState({
    inventory_number: '',
    brand_id: null as number | null,
    model_id: null as number | null,
    brand: '',
    model: '',
    serial_number: '',
    storage: '',
    storage_type: '',
    ram: '',
    processor: '',
    graphics_card: '',
    pro_support_enabled: false,
    pro_support_end_date: '',
    purchase_date: '',
    warranty_end_date: '',
    invoice: null as File | null,
    invoice_url: '',
    purchase_price: 0,
    currency: 'TRY',
    location_id: null as number | null,
    company_id: null as number | null,
    assigned_user_id: null as number | null,
    status: 'active' as 'active' | 'inactive' | 'under_repair' | 'damaged' | 'retired',
    notes: ''
  });

  const [brandData, setBrandData] = useState({
    name: '',
    description: '',
    logo: null as File | null,
    is_active: true
  });

  const statusOptions = [
    { value: 'active', label: t('computer.statusActive'), color: 'bg-green-100 text-green-800' },
    { value: 'inactive', label: t('computer.statusInactive'), color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' },
    { value: 'under_repair', label: t('computer.statusRepair'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'damaged', label: t('computer.statusDamaged'), color: 'bg-red-100 text-red-800' },
    { value: 'retired', label: t('computer.statusRetired'), color: 'bg-purple-100 text-purple-800' },
    { value: 'scrapped', label: t('computer.statusScrapped'), color: 'bg-stone-100 text-stone-800' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');

      const [computersRes, brandsRes, modelsRes, companiesRes, locationsRes, usersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/computers`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/computers/computer-brands`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/computers/computer-models`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/companies/dropdown`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/locations/dropdown`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/users`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      // Parse JSON responses
      const [computersData, brandsData, modelsData, companiesData, locationsData, usersData] = await Promise.all([
        computersRes.json(),
        brandsRes.json(),
        modelsRes.json(),
        companiesRes.json(),
        locationsRes.json(),
        usersRes.json()
      ]);

      // Set state with proper array checks
      setComputers(Array.isArray(computersData) ? computersData : []);
      setBrands(Array.isArray(brandsData) ? brandsData : []);
      setModels(Array.isArray(modelsData) ? modelsData : []);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
      setLocations(Array.isArray(locationsData) ? locationsData : []);
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('common.error'), t('computer.notifLoadError'));
      // Reset to empty arrays on error
      setComputers([]);
      setBrands([]);
      setModels([]);
      setCompanies([]);
      setLocations([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveComputer = async () => {
    // Validation
    if (!computerData.inventory_number || !computerData.brand || !computerData.model || !computerData.serial_number) {
      showGlobalNotification.error(t('common.error'), t('computer.notifRequiredFields'));
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const url = editingComputer
        ? `${API_BASE_URL}/computers/${editingComputer.id}`
        : `${API_BASE_URL}/computers`;

      const formData = new FormData();
      formData.append('inventory_number', computerData.inventory_number);
      if (computerData.brand_id) formData.append('brand_id', computerData.brand_id.toString());
      if (computerData.model_id) formData.append('model_id', computerData.model_id.toString());
      formData.append('brand', computerData.brand);
      formData.append('model', computerData.model);
      formData.append('serial_number', computerData.serial_number);
      if (computerData.storage) formData.append('storage', computerData.storage);
      if (computerData.storage_type) formData.append('storage_type', computerData.storage_type);
      if (computerData.ram) formData.append('ram', computerData.ram);
      if (computerData.processor) formData.append('processor', computerData.processor);
      if (computerData.graphics_card) formData.append('graphics_card', computerData.graphics_card);
      formData.append('pro_support_enabled', computerData.pro_support_enabled ? '1' : '0');
      if (computerData.pro_support_end_date) formData.append('pro_support_end_date', computerData.pro_support_end_date);
      if (computerData.purchase_date) formData.append('purchase_date', computerData.purchase_date);
      if (computerData.warranty_end_date) formData.append('warranty_end_date', computerData.warranty_end_date);
      if (computerData.purchase_price) formData.append('purchase_price', computerData.purchase_price.toString());
      formData.append('currency', computerData.currency || 'TRY');
      if (computerData.location_id) formData.append('location_id', computerData.location_id.toString());
      if (computerData.company_id) formData.append('company_id', computerData.company_id.toString());
      if (computerData.assigned_user_id) formData.append('assigned_user_id', computerData.assigned_user_id.toString());
      formData.append('status', computerData.status);
      if (computerData.notes) formData.append('notes', computerData.notes);

      if (computerData.invoice) {
        formData.append('invoice', computerData.invoice);
      }

      const response = await fetch(url, {
        method: editingComputer ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Kayıt başarısız');
      }

      showGlobalNotification.success(t('common.success'), editingComputer ? t('computer.notifUpdateSuccess') : t('computer.notifSaveSuccess'));
      setShowModal(false);
      resetComputerForm();
      loadData();
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    }
  };

  const handleDeleteComputer = async (id: number) => {
    if (!confirm(t('computer.confirmDelete'))) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/computers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('delete_failed');

      showGlobalNotification.success(t('common.success'), t('computer.notifDeleteSuccess'));
      loadData();
    } catch (error) {
      showGlobalNotification.error(t('common.error'), t('computer.notifDeleteFail'));
    }
  };

  // Zimmet formu oluşturma fonksiyonu
  const createAssignmentForm = async (computer: Computer, userId: string, notes: string = '', openInNewTab: boolean = true) => {
    try {
      const response = await fetch(`${API_BASE_URL}/assignment-forms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          computer_id: computer.id,
          assigned_user_id: userId,
          notes: notes
        })
      });

      if (!response.ok) {
        throw new Error('form_creation_failed');
      }

      const form = await response.json();
      showGlobalNotification.success(t('common.success'), t('computer.notifAssignFormCreated'));

      // Formu yeni sekmede aç (sadece istenirse)
      if (openInNewTab) {
        window.open(`/assignment-forms/${form.id}`, '_blank');
      }

      return form;
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
      throw error;
    }
  };

  // Atama işlemi sırasında zimmet formu onayı
  const handleAssignmentWithForm = (computer: Computer, userId: string, userName: string) => {
    setPendingAssignment({ computer, userId, userName });
    setAssignmentFormNotes('');
    setShowAssignmentFormModal(true);
    setShowAssignModal(false);
  };

  // Zimmet formu onaylandığında
  const confirmAssignmentWithForm = async () => {
    if (!pendingAssignment) return;

    try {
      // Önce zimmet formu oluştur (NOT: Atama işlemi yapılmadan!)
      const form = await createAssignmentForm(pendingAssignment.computer!, pendingAssignment.userId, assignmentFormNotes, false);

      showGlobalNotification.success(t('common.success'), t('computer.notifAssignWithFormSuccess'));

      // Modal'ı kapat
      setShowAssignmentFormModal(false);
      setPendingAssignment(null);
      setAssignmentFormNotes('');
      setSelectedUserId(null);

      // Verileri yenile
      loadData();

      // Zimmet formları sayfasına yönlendir
      navigate('assignment-forms');
    } catch (error) {
      console.error('Assignment form creation error:', error);
      showGlobalNotification.error(t('common.error'), t('computer.notifAssignError'));
    }
  };

  // Zimmet formu olmadan atama
  const confirmAssignmentWithoutForm = async () => {
    if (!pendingAssignment) return;

    try {
      const response = await fetch(`${API_BASE_URL}/computers/${pendingAssignment.computer!.id}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          user_id: parseInt(pendingAssignment.userId)
        })
      });

      if (response.ok) {
        showGlobalNotification.success(t('common.success'), t('computer.notifAssignSuccess'));
        setShowAssignmentFormModal(false);
        setPendingAssignment(null);
        setAssignmentFormNotes('');
        setSelectedUserId(null);
        loadData();
      } else {
        const errorData = await response.json();
        showGlobalNotification.error(t('common.error'), errorData.error || t('computer.notifAssignFail'));
      }
    } catch (error) {
      console.error('Assignment without form error:', error);
      showGlobalNotification.error(t('common.error'), t('computer.notifAssignError'));
    }
  };

  const handleAssignComputer = async () => {
    if (!selectedComputer || !selectedUserId) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/computers/${selectedComputer.id}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: selectedUserId })
      });

      if (!response.ok) throw new Error('assign_failed');

      showGlobalNotification.success(t('common.success'), t('computer.notifAssignSuccess'));
      setShowAssignModal(false);
      setSelectedComputer(null);
      setSelectedUserId(null);
      loadData();
    } catch (error) {
      showGlobalNotification.error(t('common.error'), t('computer.notifAssignFail'));
    }
  };

  // Base64 fotoğrafları form ID'ye yükle
  const uploadReturnPhotos = async (formId: number, photos: string[]) => {
    if (photos.length === 0) return;
    const token = localStorage.getItem('auth_token');
    const fd = new FormData();
    photos.forEach((dataUrl, i) => {
      const arr = dataUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)![1];
      const bstr = atob(arr[1]);
      const u8arr = new Uint8Array(bstr.length);
      for (let n = 0; n < bstr.length; n++) u8arr[n] = bstr.charCodeAt(n);
      fd.append('photos', new Blob([u8arr], { type: mime }), `photo-${i + 1}.jpg`);
    });
    await fetch(`${API_BASE_URL}/assignment-forms/${formId}/return-photos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: fd
    });
  };

  // Geri zimmet formu oluşturma fonksiyonu
  const createReturnForm = async (computer: Computer, notes: string = '') => {
    try {
      const response = await fetch(`${API_BASE_URL}/assignment-forms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          computer_id: computer.id,
          assigned_user_id: computer.assigned_user_id,
          form_type: 'return',
          notes: notes
        })
      });

      if (!response.ok) {
        throw new Error('return_form_creation_failed');
      }

      const form = await response.json();
      showGlobalNotification.success(t('common.success'), t('computer.notifAssignFormCreated'));

      return form;
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
      throw error;
    }
  };

  // Atama kaldırma modal'ını aç
  const handleUnassignComputer = (computer: Computer) => {
    setPendingUnassignment(computer);
    setUnassignmentFormNotes('');
    setUnassignmentPhotos([]);
    setShowUnassignModal(true);
  };

  // Geri zimmet fotoğrafı ekle
  const addUnassignmentPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const remaining = 4 - unassignmentPhotos.length;
    const toProcess = Math.min(files.length, remaining);
    for (let i = 0; i < toProcess; i++) {
      const file = files[i];
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxW = 1200, maxH = 1600;
          let { width, height } = img;
          if (width > maxW || height > maxH) {
            const ratio = Math.min(maxW / width, maxH / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
          setUnassignmentPhotos(prev => prev.length < 4 ? [...prev, dataUrl] : prev);
        };
        img.src = ev.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  // Zimmet geri teslim formu ile atama kaldırma
  const confirmUnassignmentWithForm = async () => {
    if (!pendingUnassignment) return;

    try {
      // Önce geri zimmet formu oluştur
      const form = await createReturnForm(pendingUnassignment, unassignmentFormNotes);

      // Fotoğrafları ayrıca yükle
      if (unassignmentPhotos.length > 0) {
        await uploadReturnPhotos(form.id, unassignmentPhotos);
      }

      showGlobalNotification.success(t('common.success'), t('computer.notifReturnFormCreated'));

      // Modal'ı kapat
      setShowUnassignModal(false);
      setPendingUnassignment(null);
      setUnassignmentFormNotes('');
      setUnassignmentPhotos([]);

      // Verileri yenile
      loadData();

      // Zimmet formları sayfasına yönlendir
      navigate('assignment-forms');
    } catch (error) {
      console.error('Return form creation error:', error);
      showGlobalNotification.error(t('common.error'), t('computer.notifReturnFormError'));
    }
  };

  // Form olmadan atama kaldırma
  const confirmUnassignmentWithoutForm = async () => {
    if (!pendingUnassignment) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/computers/${pendingUnassignment.id}/unassign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('unassign_failed');

      showGlobalNotification.success(t('common.success'), t('computer.notifUnassignSuccess'));
      setShowUnassignModal(false);
      setPendingUnassignment(null);
      setUnassignmentFormNotes('');
      setUnassignmentPhotos([]);
      loadData();
    } catch (error) {
      showGlobalNotification.error(t('common.error'), t('common.operationFailed'));
    }
  };

  const handleSaveBrand = async () => {
    // Validation
    if (!brandData.name.trim()) {
      showGlobalNotification.error(t('common.error'), t('computer.notifBrandRequired'));
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('name', brandData.name);
      formData.append('description', brandData.description);
      formData.append('is_active', brandData.is_active.toString());
      if (brandData.logo) {
        formData.append('logo', brandData.logo);
      }

      const url = editingBrand
        ? `${API_BASE_URL}/computers/computer-brands/${editingBrand.id}`
        : `${API_BASE_URL}/computers/computer-brands`;

      const response = await fetch(url, {
        method: editingBrand ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Kayıt başarısız');
      }

      showGlobalNotification.success(t('common.success'), editingBrand ? t('computer.notifBrandUpdated') : t('computer.notifBrandSaved'));
      setShowBrandModal(false);
      resetBrandForm();
      loadData();
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    }
  };


  const handleDeleteBrand = async (id: number) => {
    if (!confirm(t('computer.confirmDeleteBrand'))) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/computers/computer-brands/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Silme başarısız');
      }

      showGlobalNotification.success(t('common.success'), t('computer.notifBrandDeleted'));
      loadData();
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    }
  };

  const handleBrandChange = (brandId: string) => {
    if (!brandId) {
      setComputerData({ ...computerData, brand_id: null, model_id: null, brand: '', model: '' });
      setFilteredModels([]);
      return;
    }


    const selectedBrand = brands.find(b => b.id === parseInt(brandId));
    const brandModels = models.filter(m => m.brand_id === parseInt(brandId) && m.is_active);

    setComputerData({
      ...computerData,
      brand_id: parseInt(brandId),
      brand: selectedBrand?.name || '',
      model_id: null,
      model: ''
    });
    setFilteredModels(brandModels);
  };

  const handleModelChange = (modelId: string) => {
    if (!modelId) {
      setComputerData({ ...computerData, model_id: null, model: '' });
      return;
    }

    const selectedModel = models.find(m => m.id === parseInt(modelId));
    setComputerData({
      ...computerData,
      model_id: parseInt(modelId),
      model: selectedModel?.name || '',
      processor: selectedModel?.processor || computerData.processor,
      graphics_card: selectedModel?.graphics_card || computerData.graphics_card,
      storage: selectedModel?.storage_capacity || computerData.storage,
      storage_type: selectedModel?.storage_type || computerData.storage_type,
      ram: selectedModel?.ram || computerData.ram
    });
  };

  const resetComputerForm = () => {
    setComputerData({
      inventory_number: '',
      brand_id: null,
      model_id: null,
      brand: '',
      model: '',
      serial_number: '',
      storage: '',
      storage_type: '',
      ram: '',
      processor: '',
      graphics_card: '',
      pro_support_enabled: false,
      pro_support_end_date: '',
      purchase_date: '',
      warranty_end_date: '',
      invoice: null,
      invoice_url: '',
      purchase_price: 0,
      currency: 'TRY',
      location_id: null,
      company_id: null,
      assigned_user_id: null,
      status: 'active',
      notes: ''
    });
    setFilteredModels([]);
    setEditingComputer(null);
  };

  const resetBrandForm = () => {
    setBrandData({ name: '', description: '', logo: null, is_active: true });
    setEditingBrand(null);
  };

  const handleEditComputer = (computer: Computer) => {
    setEditingComputer(computer);
    
    // Format dates to YYYY-MM-DD (remove time part if exists)
    const formatDate = (dateStr: string | undefined) => {
      if (!dateStr) return '';
      // ISO format check (T) and space check
      if (dateStr.includes('T')) return dateStr.split('T')[0];
      return dateStr.split(' ')[0]; // Get only the date part (YYYY-MM-DD)
    };
    
    setComputerData({
      inventory_number: computer.inventory_number,
      brand_id: computer.brand_id || null,
      model_id: computer.model_id || null,
      brand: computer.brand,
      model: computer.model,
      serial_number: computer.serial_number,
      storage: computer.storage || '',
      storage_type: computer.storage_type || '',
      ram: computer.ram || '',
      processor: computer.processor || '',
      graphics_card: computer.graphics_card || '',
      pro_support_enabled: Boolean(computer.pro_support_enabled),
      pro_support_end_date: formatDate(computer.pro_support_end_date),
      purchase_date: formatDate(computer.purchase_date),
      warranty_end_date: formatDate(computer.warranty_end_date),
      invoice: null,
      invoice_url: computer.invoice_url || '',
      purchase_price: computer.purchase_price || 0,
      currency: computer.currency || 'TRY',
      location_id: computer.location_id || null,
      company_id: computer.company_id || null,
      assigned_user_id: computer.assigned_user_id || null,
      status: computer.status,
      notes: computer.notes || ''
    });

    // Filter models for the selected brand
    if (computer.brand_id) {
      const brandModels = models.filter(m => m.brand_id === computer.brand_id && m.is_active);
      setFilteredModels(brandModels);
    } else {
      setFilteredModels([]);
    }

    setShowModal(true);
  };

  const handleCopyComputer = (computer: Computer) => {
    setEditingComputer(null);

    if (computer.brand_id) {
      const brandModels = models.filter(m => m.brand_id === computer.brand_id && m.is_active);
      setFilteredModels(brandModels);
    } else {
      setFilteredModels([]);
    }

    setComputerData({
      inventory_number: '',
      brand_id: computer.brand_id || null,
      model_id: computer.model_id || null,
      brand: computer.brand,
      model: computer.model,
      serial_number: '',
      storage: computer.storage || '',
      storage_type: computer.storage_type || '',
      ram: computer.ram || '',
      processor: computer.processor || '',
      graphics_card: computer.graphics_card || '',
      pro_support_enabled: false,
      pro_support_end_date: '',
      purchase_date: '',
      warranty_end_date: '',
      invoice: null,
      invoice_url: '',
      purchase_price: 0,
      currency: computer.currency || 'TRY',
      location_id: computer.location_id || null,
      company_id: computer.company_id || null,
      assigned_user_id: null,
      status: 'active',
      notes: ''
    });

    setShowModal(true);
  };

  const handleEditBrand = (brand: Brand) => {
    setEditingBrand(brand);
    setBrandData({
      name: brand.name,
      description: brand.description || '',
      logo: null,
      is_active: brand.is_active
    });
    setShowBrandModal(true);
  };

  // Bilgisayar geçmişini getir
  const fetchComputerHistory = async (computer: Computer) => {
    setSelectedComputerForHistory(computer);
    setHistoryLoading(true);
    setShowHistoryModal(true);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/computers/${computer.id}/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setComputerHistory(data);
      } else {
        showGlobalNotification.error(t('common.error'), t('computer.notifHistoryLoadFail'));
        setShowHistoryModal(false);
      }
    } catch (error) {
      console.error('Error fetching computer history:', error);
      showGlobalNotification.error(t('common.error'), t('computer.notifHistoryLoadError'));
      setShowHistoryModal(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  // İşlem türüne göre renk ve ikon
  const getActionStyle = (actionType: string) => {
    switch (actionType) {
      case 'create':
        return { color: 'text-green-600', bg: 'bg-green-100', icon: <Plus className="w-4 h-4" />, label: t('computer.actionCreated') };
      case 'update':
        return { color: 'text-primary', bg: 'bg-primary/20', icon: <Edit className="w-4 h-4" />, label: t('computer.actionUpdated') };
      case 'assign':
        return { color: 'text-purple-600', bg: 'bg-purple-100', icon: <UserPlus className="w-4 h-4" />, label: t('computer.actionAssigned') };
      case 'unassign':
        return { color: 'text-orange-600', bg: 'bg-orange-100', icon: <UserMinus className="w-4 h-4" />, label: t('computer.actionUnassigned') };
      case 'delete':
        return { color: 'text-red-600', bg: 'bg-red-100', icon: <Trash2 className="w-4 h-4" />, label: t('computer.actionDeleted') };
      default:
        return { color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-700', icon: <Clock className="w-4 h-4" />, label: actionType };
    }
  };

  // Hurda formu status değişikliği interceptor
  const handleStatusChange = (newStatus: string) => {
    if (newStatus === 'scrapped' && editingComputer) {
      // Hurda seçildi, modal aç
      setPendingScrapComputer(editingComputer);
      setScrapFormData({
        ...scrapFormData,
        device_type: 'desktop',
        hardware_checklist: {
          cpu: { status: 'exists', detail: editingComputer.processor || '', spare_part: false },
          ram: { status: 'exists', detail: editingComputer.ram || '', spare_part: false },
          storage: { status: 'exists', detail: editingComputer.storage || '', spare_part: false },
          screen: { status: 'na', detail: '', spare_part: false },
          motherboard: { status: 'working', detail: '', spare_part: false },
          battery: { status: 'na', detail: '', spare_part: false },
          adapter: { status: 'exists', detail: '', spare_part: false },
          case_condition: { status: 'good', detail: '', spare_part: false },
          keyboard: { status: 'working', detail: '', spare_part: false },
          ports: { status: 'good', detail: '', spare_part: false }
        },
        scrap_reasons: [],
        scrap_reason_detail: '',
        disk_removed: false,
        data_wipe_method: 'na',
        media_check_done: false,
        data_security_notes: '',
        photos: []
      });
      setScrapFormStep(1);
      setShowScrapModal(true);
      setShowModal(false);
    } else {
      setComputerData({ ...computerData, status: newStatus as any });
    }
  };

  // Hurda formunu gönder
  const handleSubmitScrapForm = async () => {
    if (!pendingScrapComputer) return;

    setScrapFormLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();

      formData.append('device_type', scrapFormData.device_type);
      formData.append('hardware_checklist', JSON.stringify(scrapFormData.hardware_checklist));
      formData.append('scrap_reasons', JSON.stringify(scrapFormData.scrap_reasons));
      formData.append('scrap_reason_detail', scrapFormData.scrap_reason_detail);
      formData.append('disk_removed', scrapFormData.disk_removed.toString());
      formData.append('data_wipe_method', scrapFormData.data_wipe_method);
      formData.append('media_check_done', scrapFormData.media_check_done.toString());
      formData.append('data_security_notes', scrapFormData.data_security_notes);

      // Fotoğrafları ekle
      scrapFormData.photos.forEach((photo) => {
        formData.append('photos', photo);
      });

      const response = await fetch(`${API_BASE_URL}/computers/${pendingScrapComputer.id}/scrap`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'scrap_failed');
      }

      const result = await response.json();
      showGlobalNotification.success(t('common.success'), `${t('computer.notifScrapSuccess')}${result.form.form_number}`);

      setShowScrapModal(false);
      setPendingScrapComputer(null);
      loadData();
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    } finally {
      setScrapFormLoading(false);
    }
  };

  // Hurda form fotoğraf ekleme
  const handleScrapPhotoAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newPhotos = [...scrapFormData.photos, ...Array.from(files)].slice(0, 5);
      setScrapFormData({ ...scrapFormData, photos: newPhotos });
    }
  };

  // Hurda form fotoğraf silme
  const handleScrapPhotoRemove = (index: number) => {
    const newPhotos = scrapFormData.photos.filter((_, i) => i !== index);
    setScrapFormData({ ...scrapFormData, photos: newPhotos });
  };

  const exportToExcel = () => {
    const exportData = filteredComputers.map(computer => ({
      [t('computer.colInventoryNo')]: computer.inventory_number,
      [t('computer.fieldBrand')]: computer.brand,
      [t('computer.fieldModel')]: computer.model,
      [t('computer.colSerialNo')]: computer.serial_number,
      [t('computer.storage')]: computer.storage || '-',
      [t('computer.fieldRAM')]: computer.ram || '-',
      [t('computer.fieldProcessor')]: computer.processor || '-',
      [t('computer.fieldGraphicsCard')]: computer.graphics_card || '-',
      [t('computer.fieldCompany')]: computer.company_name || '-',
      [t('computer.fieldLocation')]: computer.location_name || '-',
      [t('computer.colAssignedUser')]: computer.assigned_user_name || '-',
      [t('common.status')]: statusOptions.find(s => s.value === computer.status)?.label || computer.status,
      [t('computer.fieldPurchaseDate')]: computer.purchase_date || '-',
      [t('computer.fieldWarrantyEnd')]: computer.warranty_end_date || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('computer.excelSheetName'));
    XLSX.writeFile(wb, `${t('computer.excelFileName')}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Sorting function
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Sort icon component
  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <ChevronUp className="w-4 h-4 text-gray-300" />;
    }
    return sortDirection === 'asc'
      ? <ChevronUp className="w-4 h-4 text-primary" />
      : <ChevronDown className="w-4 h-4 text-primary" />;
  };

  // QR Code generation function
  const generateQRCode = (computer: Computer) => {
    const token = localStorage.getItem('auth_token');
    const params = new URLSearchParams({
      token: token || '',
    });
    const siteUrl = import.meta.env.VITE_SITE_URL || `${window.location.protocol}//${window.location.host}`;
    const url = `${siteUrl}/api/computers/${computer.id}/qr-code?${params}`;

    // Yeni sekmede aç
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.location.href = url;
    } else {
      window.location.href = url;
    }
  };

  // Lokasyon bazlı bilgisayar sayıları
  const locationStats = locations.map(loc => ({
    ...loc,
    count: computers.filter(c => c.location_id === loc.id).length
  })).filter(loc => loc.count > 0);

  // Yaş eşiği hesaplamaları (configurable)
  const now = new Date();
  const fiveYearsAgo = new Date(now.getFullYear() - ageThresholdYears, now.getMonth(), now.getDate());
  const threeMonthsFromNow = new Date(now.getFullYear(), now.getMonth() + warningMonths, now.getDate());
  const fiveYearsFromThreeMonths = new Date(threeMonthsFromNow.getFullYear() - ageThresholdYears, threeMonthsFromNow.getMonth(), threeMonthsFromNow.getDate());

  // Eşiği doldurmuş bilgisayarlar
  const over5YearsComputers = computers.filter(c => {
    if (!c.purchase_date) return false;
    const purchaseDate = new Date(c.purchase_date);
    return purchaseDate <= fiveYearsAgo;
  });

  // Uyarı süresi içinde eşiğe yaklaşan bilgisayarlar
  const near5YearsComputers = computers.filter(c => {
    if (!c.purchase_date) return false;
    const purchaseDate = new Date(c.purchase_date);
    return purchaseDate > fiveYearsAgo && purchaseDate <= fiveYearsFromThreeMonths;
  });

  const filteredComputers = computers.filter(computer => {
    const matchesSearch = searchTerm === '' ||
      computer.inventory_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      computer.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
      computer.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      computer.serial_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      computer.assigned_user_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || computer.status === filterStatus;
    const matchesCompany = filterCompany === 'all' || computer.company_id?.toString() === filterCompany;
    const matchesLocation = locationFilter === 'all' || computer.location_id?.toString() === locationFilter;

    // Age filter
    let matchesAge = true;
    if (ageFilter === 'over5years') {
      if (!computer.purchase_date) {
        matchesAge = false;
      } else {
        const purchaseDate = new Date(computer.purchase_date);
        matchesAge = purchaseDate <= fiveYearsAgo;
      }
    } else if (ageFilter === 'near5years') {
      if (!computer.purchase_date) {
        matchesAge = false;
      } else {
        const purchaseDate = new Date(computer.purchase_date);
        matchesAge = purchaseDate > fiveYearsAgo && purchaseDate <= fiveYearsFromThreeMonths;
      }
    }

    return matchesSearch && matchesStatus && matchesCompany && matchesLocation && matchesAge;
  });

  // Apply sorting
  const sortedComputers = [...filteredComputers].sort((a, b) => {
    let aValue: any = a[sortField as keyof Computer];
    let bValue: any = b[sortField as keyof Computer];

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    // Convert to lowercase for string comparison
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedComputers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedComputers = sortedComputers.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterCompany, locationFilter, ageFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
          <Monitor className="mr-2 w-5 h-5 md:w-6 md:h-6" />
          {t('computer.pageTitle')}
        </h1>
        <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1">
          {computers.length} {t('computer.totalCount')}
        </p>
      </div>

      {/* Location Cards */}
      {locationStats.length > 0 && (
        <div className="mb-4 md:mb-6">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5 text-primary" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('computer.locationStats')}</h2>
            {locationFilter !== 'all' && (
              <button
                onClick={() => setLocationFilter('all')}
                className="ml-2 text-xs text-primary hover:text-primary-dark underline"
              >
                {t('computer.clearFilter')}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {locationStats.map(loc => (
              <div
                key={loc.id}
                onClick={() => setLocationFilter(locationFilter === loc.id.toString() ? 'all' : loc.id.toString())}
                className={`bg-white dark:bg-gray-800 rounded-xl p-4 cursor-pointer transition-all border-2 hover:shadow-lg ${
                  locationFilter === loc.id.toString()
                    ? 'border-primary ring-2 ring-primary/30 dark:ring-primary-dark shadow-md'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{loc.name}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{loc.count}</p>
                  </div>
                  <div className={`p-2 rounded-lg ${locationFilter === loc.id.toString() ? 'bg-primary/20 dark:bg-primary/90' : 'bg-gray-100 dark:bg-gray-700'}`}>
                    <Laptop className={`w-5 h-5 ${locationFilter === loc.id.toString() ? 'text-primary' : 'text-gray-500 dark:text-gray-400'}`} />
                  </div>
                </div>
                {locationFilter === loc.id.toString() && (
                  <div className="mt-2 text-xs text-primary dark:text-primary font-medium">{t('computer.filterActive')}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Age-based Cards (5 Year Tracking) */}
      {(over5YearsComputers.length > 0 || near5YearsComputers.length > 0) && (
        <div className="mb-4 md:mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-orange-600" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {language === 'en'
                ? `By Age (${ageThresholdYears} Year Tracking)`
                : `Yaşa Göre (${ageThresholdYears} Yıl Takibi)`}
            </h2>
            {ageFilter !== 'all' && (
              <button
                onClick={() => setAgeFilter('all')}
                className="ml-2 text-xs text-orange-600 hover:text-orange-800 underline"
              >
                {t('computer.clearFilter')}
              </button>
            )}
            <button
              onClick={() => { setAgeSettingsDraft({ years: ageThresholdYears, months: warningMonths }); setShowAgeSettings(true); }}
              className="ml-auto p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded"
              title={language === 'en' ? 'Age Filter Settings' : 'Yaş Filtresi Ayarları'}
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>

          {/* Age Settings Panel */}
          {showAgeSettings && (
            <div className="mb-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4 max-w-xl">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {language === 'en' ? 'Age Filter Settings' : 'Yaş Filtresi Ayarları'}
                </span>
                <button onClick={() => setShowAgeSettings(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    {language === 'en' ? 'Age Threshold (years)' : 'Yaş Eşiği (yıl)'}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={ageSettingsDraft.years}
                    onChange={(e) => setAgeSettingsDraft(prev => ({ ...prev, years: Math.max(1, parseInt(e.target.value) || 1) }))}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    {language === 'en' ? 'Warning Period (months)' : 'Uyarı Süresi (ay)'}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={24}
                    value={ageSettingsDraft.months}
                    onChange={(e) => setAgeSettingsDraft(prev => ({ ...prev, months: Math.max(1, parseInt(e.target.value) || 1) }))}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-3 gap-2">
                <button
                  onClick={() => setShowAgeSettings(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 border border-gray-300 dark:border-gray-600 rounded-md"
                >
                  {language === 'en' ? 'Cancel' : 'İptal'}
                </button>
                <button
                  onClick={() => {
                    setAgeThresholdYears(ageSettingsDraft.years);
                    setWarningMonths(ageSettingsDraft.months);
                    localStorage.setItem('computers_ageThresholdYears', String(ageSettingsDraft.years));
                    localStorage.setItem('computers_warningMonths', String(ageSettingsDraft.months));
                    setAgeFilter('all');
                    setShowAgeSettings(false);
                    showGlobalNotification.success(language === 'en' ? 'Settings saved' : 'Ayarlar kaydedildi', '');
                  }}
                  className="px-3 py-1.5 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-md"
                >
                  {language === 'en' ? 'Save' : 'Kaydet'}
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
            {/* Eşiği Doldurmuş */}
            <div
              onClick={() => setAgeFilter(ageFilter === 'over5years' ? 'all' : 'over5years')}
              className={`bg-white dark:bg-gray-800 rounded-xl p-4 cursor-pointer transition-all border-2 hover:shadow-lg ${
                ageFilter === 'over5years'
                  ? 'border-red-500 ring-2 ring-red-200 dark:ring-red-800 shadow-md'
                  : 'border-gray-200 dark:border-gray-700 hover:border-red-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {language === 'en' ? `Over ${ageThresholdYears} Years Old` : `${ageThresholdYears} Yılını Doldurmuş`}
                  </p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">{over5YearsComputers.length}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('computer.renewalRecommended')}</p>
                </div>
                <div className={`p-3 rounded-lg ${ageFilter === 'over5years' ? 'bg-red-100 dark:bg-red-900' : 'bg-red-50 dark:bg-red-900/30'}`}>
                  <AlertCircle className={`w-6 h-6 ${ageFilter === 'over5years' ? 'text-red-600' : 'text-red-400'}`} />
                </div>
              </div>
              {ageFilter === 'over5years' && (
                <div className="mt-2 text-xs text-red-600 dark:text-red-400 font-medium">{t('computer.filterActive')}</div>
              )}
            </div>

            {/* Uyarı Süresi İçinde Eşiğe Yaklaşan */}
            <div
              onClick={() => setAgeFilter(ageFilter === 'near5years' ? 'all' : 'near5years')}
              className={`bg-white dark:bg-gray-800 rounded-xl p-4 cursor-pointer transition-all border-2 hover:shadow-lg ${
                ageFilter === 'near5years'
                  ? 'border-orange-500 ring-2 ring-orange-200 dark:ring-orange-800 shadow-md'
                  : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {language === 'en'
                      ? `Approaching ${ageThresholdYears} Years (${warningMonths} Months)`
                      : `${warningMonths} Ay İçinde ${ageThresholdYears} Yılını Dolduracak`}
                  </p>
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">{near5YearsComputers.length}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('computer.nearRenewal')}</p>
                </div>
                <div className={`p-3 rounded-lg ${ageFilter === 'near5years' ? 'bg-orange-100 dark:bg-orange-900' : 'bg-orange-50 dark:bg-orange-900/30'}`}>
                  <Clock className={`w-6 h-6 ${ageFilter === 'near5years' ? 'text-orange-600' : 'text-orange-400'}`} />
                </div>
              </div>
              {ageFilter === 'near5years' && (
                <div className="mt-2 text-xs text-orange-600 dark:text-orange-400 font-medium">{t('computer.filterActive')}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className="mb-4 md:mb-6 flex flex-wrap gap-2 md:gap-4">
        <div className="flex-1 min-w-[200px] sm:min-w-[300px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t('computer.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="all">{t('computer.allStatuses')}</option>
          {statusOptions.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>

        <select
          value={filterCompany}
          onChange={(e) => setFilterCompany(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="all">{t('computer.allCompanies')}</option>
          {companies.map(company => (
            <option key={company.id} value={company.id}>{company.name}</option>
          ))}
        </select>

        <button
          onClick={() => { resetComputerForm(); setShowModal(true); }}
          className="px-3 py-2 md:px-4 bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center text-sm md:text-base whitespace-nowrap"
        >
          <Plus className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
          <span className="hidden sm:inline">{t('computer.newComputer')}</span>
          <span className="sm:hidden">{t('common.add')}</span>
        </button>

        <button
          onClick={() => { resetBrandForm(); setShowBrandModal(true); }}
          className="px-3 py-2 md:px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center text-sm md:text-base whitespace-nowrap"
        >
          <Settings className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
          <span className="hidden sm:inline">{t('computer.brandManagement')}</span>
          <span className="sm:hidden">{t('computer.fieldBrand').replace(' *', '')}</span>
        </button>

        <button
          onClick={() => navigate('/computer-models')}
          className="px-3 py-2 md:px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center text-sm md:text-base whitespace-nowrap"
        >
          <Settings className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
          <span className="hidden sm:inline">{t('computer.modelManagement')}</span>
          <span className="sm:hidden">{t('computer.fieldModel').replace(' *', '')}</span>
        </button>

        <button
          onClick={exportToExcel}
          className="px-3 py-2 md:px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center text-sm md:text-base whitespace-nowrap"
        >
          <Download className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
          <span className="hidden sm:inline">{t('computer.excelDownload')}</span>
          <span className="sm:hidden">Excel</span>
        </button>

        <button
          onClick={() => {
            window.dispatchEvent(new CustomEvent('navigate', { detail: 'scrap-computers' }));
          }}
          className="px-3 py-2 md:px-4 bg-stone-600 text-white rounded-lg hover:bg-stone-700 flex items-center text-sm md:text-base whitespace-nowrap"
        >
          <Trash2 className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
          <span className="hidden sm:inline">{t('computer.scrapComputers')}</span>
          <span className="sm:hidden">{t('computer.scrapStep3')}</span>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 dark:bg-gray-900">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-800 select-none"
                  onClick={() => handleSort('inventory_number')}
                >
                  <div className="flex items-center gap-1">
                    {t('computer.colInventoryNo')}
                    <SortIcon field="inventory_number" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-800 select-none"
                  onClick={() => handleSort('brand')}
                >
                  <div className="flex items-center gap-1">
                    {t('computer.colBrandModel')}
                    <SortIcon field="brand" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-800 select-none"
                  onClick={() => handleSort('serial_number')}
                >
                  <div className="flex items-center gap-1">
                    {t('computer.colSerialNo')}
                    <SortIcon field="serial_number" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('computer.colSpecs')}</th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-800 select-none"
                  onClick={() => handleSort('company_name')}
                >
                  <div className="flex items-center gap-1">
                    {t('computer.colCompanyLocation')}
                    <SortIcon field="company_name" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-800 select-none"
                  onClick={() => handleSort('assigned_user_name')}
                >
                  <div className="flex items-center gap-1">
                    {t('computer.colAssignedUser')}
                    <SortIcon field="assigned_user_name" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-800 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    {t('common.status')}
                    <SortIcon field="status" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedComputers.map((computer) => {
                const computerModel = models.find((m: any) => m.name === computer.model && (m.brand_name === computer.brand || !m.brand_name));
                return (
                <tr key={computer.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {computer.inventory_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {computerModel?.image_url ? (
                        <img src={computerModel.image_url} alt={computer.model} className="w-10 h-10 object-contain rounded-lg bg-white mr-3 border border-gray-200" />
                      ) : (
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                          <Monitor className="w-5 h-5 text-blue-600" />
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{computer.brand}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{computer.model}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {computer.serial_number}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {computer.ram && <div>RAM: {computer.ram}</div>}
                      {computer.storage && <div>{t('computer.storage')}: {computer.storage}</div>}
                      {computer.processor && <div className="text-xs text-gray-500 dark:text-gray-400">{computer.processor}</div>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{computer.company_name || '-'}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{computer.location_name || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {computer.assigned_user_name ? (
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{computer.assigned_user_name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{computer.assigned_user_email}</div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">{t('computer.notAssigned')}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusOptions.find(s => s.value === computer.status)?.color}`}>
                      {statusOptions.find(s => s.value === computer.status)?.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => fetchComputerHistory(computer)}
                      className="text-purple-600 hover:text-purple-900"
                      title={t('computer.titleHistory')}
                    >
                      <History className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleCopyComputer(computer)}
                      className="text-blue-500 hover:text-blue-700"
                      title={t('computer.titleCopy') || 'Kopyala'}
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleEditComputer(computer)}
                      className="text-primary hover:text-primary-dark"
                      title={t('common.edit')}
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    {computer.assigned_user_id ? (
                      <button
                        onClick={() => handleUnassignComputer(computer)}
                        className="text-orange-600 hover:text-orange-900"
                        title={t('computer.titleUnassign')}
                      >
                        <UserMinus className="w-5 h-5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => { setSelectedComputer(computer); setShowAssignModal(true); }}
                        className="text-green-600 hover:text-green-900"
                        title={t('computer.titleAssign')}
                      >
                        <UserPlus className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => generateQRCode(computer)}
                      className="text-teal-600 hover:text-teal-900"
                      title={t('computer.titleQR')}
                    >
                      <QrCode className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteComputer(computer.id)}
                      className="text-red-600 hover:text-red-900"
                      title={t('common.delete')}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {language === 'en'
                ? `Showing ${startIndex + 1}-${Math.min(endIndex, sortedComputers.length)} ${t('computer.paginationInfo')} ${sortedComputers.length} ${t('computer.paginationShowing')}`
                : `Toplam ${sortedComputers.length} ${t('computer.paginationInfo')} ${startIndex + 1}-${Math.min(endIndex, sortedComputers.length)} ${t('computer.paginationShowing')}`
              }
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-2 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700"
                title={t('computer.firstPage')}
              >
                <ChevronLeft className="w-4 h-4" />
                <ChevronLeft className="w-4 h-4 -ml-2" />
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700"
                title={t('computer.prevPage')}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    return false;
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2 text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-md text-sm font-medium ${
                          currentPage === page
                            ? 'bg-primary text-white'
                            : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700"
                title={t('computer.nextPage')}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700"
                title={t('computer.lastPage')}
              >
                <ChevronRight className="w-4 h-4" />
                <ChevronRight className="w-4 h-4 -ml-2" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Computer Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 md:p-6">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {editingComputer ? t('computer.modalTitleEdit') : t('computer.modalTitleAdd')}
                </h2>
                <button onClick={() => { setShowModal(false); resetComputerForm(); }} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300">
                  <X className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldInventoryNo')}</label>
                  <input
                    type="text"
                    value={computerData.inventory_number}
                    onChange={(e) => setComputerData({ ...computerData, inventory_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="PC-2024-001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldSerialNo')}</label>
                  <input
                    type="text"
                    value={computerData.serial_number}
                    onChange={(e) => setComputerData({ ...computerData, serial_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldBrand')}</label>
                  <select
                    value={computerData.brand_id || ''}
                    onChange={(e) => handleBrandChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">{t('computer.selectPlaceholder')}</option>
                    {brands.filter(b => b.is_active).map(brand => (
                      <option key={brand.id} value={brand.id}>{brand.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldModel')}</label>
                  <select
                    value={computerData.model_id || ''}
                    onChange={(e) => handleModelChange(e.target.value)}
                    disabled={!computerData.brand_id}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">{t('computer.selectPlaceholder')}</option>
                    {filteredModels.map(model => (
                      <option key={model.id} value={model.id}>{model.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldProcessor')}</label>
                  <input
                    type="text"
                    value={computerData.processor}
                    readOnly
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                    placeholder={t('computer.fieldProcessorPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('computer.fieldAutoFill')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldGraphicsCard')}</label>
                  <input
                    type="text"
                    value={computerData.graphics_card}
                    readOnly
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                    placeholder={t('computer.fieldProcessorPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('computer.fieldAutoFill')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldStorageCapacity')}</label>
                  <input
                    type="text"
                    value={computerData.storage}
                    readOnly
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                    placeholder={t('computer.fieldProcessorPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('computer.fieldAutoFill')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldStorageType')}</label>
                  <input
                    type="text"
                    value={computerData.storage_type}
                    readOnly
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                    placeholder={t('computer.fieldProcessorPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('computer.fieldAutoFill')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldRAM')}</label>
                  <input
                    type="text"
                    value={computerData.ram}
                    readOnly
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                    placeholder={t('computer.fieldProcessorPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('computer.fieldAutoFill')}</p>
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="pro_support_enabled"
                      checked={computerData.pro_support_enabled}
                      onChange={(e) => setComputerData({ ...computerData, pro_support_enabled: e.target.checked })}
                      className="w-4 h-4 text-primary border-gray-300 dark:border-gray-600 rounded focus:ring-primary"
                    />
                    <label htmlFor="pro_support_enabled" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('computer.fieldProSupport')}
                    </label>
                  </div>

                  {computerData.pro_support_enabled && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldProSupportEnd')}</label>
                      <input
                        type="date"
                        value={computerData.pro_support_end_date}
                        onChange={(e) => setComputerData({ ...computerData, pro_support_end_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldPurchaseDate')}</label>
                  <input
                    type="date"
                    value={computerData.purchase_date}
                    onChange={(e) => setComputerData({ ...computerData, purchase_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldWarrantyEnd')}</label>
                  <input
                    type="date"
                    value={computerData.warranty_end_date}
                    onChange={(e) => setComputerData({ ...computerData, warranty_end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldInvoice')}</label>
                  {computerData.invoice_url && !computerData.invoice && (
                    <div className="mb-2 p-3 bg-primary/10 dark:bg-primary/20 rounded-md flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <FileText className="w-5 h-5 text-primary" />
                        <span className="text-sm text-primary-dark dark:text-accent">{t('computer.invoiceExists')}</span>
                      </div>
                      <div className="flex space-x-2">
                        <a
                          href={`${API_BASE_URL}${computerData.invoice_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1 bg-primary text-white rounded text-sm hover:bg-primary-dark"
                        >
                          {t('computer.invoiceView')}
                        </a>
                      </div>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                    onChange={(e) => setComputerData({ ...computerData, invoice: e.target.files?.[0] || null })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('computer.invoiceFileHint')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldCompany')}</label>
                  <select
                    value={computerData.company_id || ''}
                    onChange={(e) => setComputerData({ ...computerData, company_id: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">{t('computer.selectPlaceholder')}</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldLocation')}</label>
                  <select
                    value={computerData.location_id || ''}
                    onChange={(e) => setComputerData({ ...computerData, location_id: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">{t('computer.selectPlaceholder')}</option>
                    {locations.map(location => (
                      <option key={location.id} value={location.id}>{location.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.status')}</label>
                  <select
                    value={computerData.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldNotes')}</label>
                  <textarea
                    value={computerData.notes}
                    onChange={(e) => setComputerData({ ...computerData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>

              <div className="mt-4 md:mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
                <button
                  onClick={() => { setShowModal(false); resetComputerForm(); }}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSaveComputer}
                  className="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                >
                  {editingComputer ? t('computer.update') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </Modal>

      {/* Brand Modal */}
      <Modal isOpen={showBrandModal} onClose={() => setShowBrandModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 md:p-6">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {editingBrand ? t('computer.brandModalTitleEdit') : t('computer.brandModalTitleAdd')}
                </h2>
                <button onClick={() => { setShowBrandModal(false); resetBrandForm(); }} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300">
                  <X className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldBrandName')}</label>
                  <input
                    type="text"
                    value={brandData.name}
                    onChange={(e) => setBrandData({ ...brandData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Dell, HP, Lenovo..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.description')}</label>
                  <textarea
                    value={brandData.description}
                    onChange={(e) => setBrandData({ ...brandData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldLogo')}</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setBrandData({ ...brandData, logo: e.target.files?.[0] || null })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="brand_is_active"
                    checked={brandData.is_active}
                    onChange={(e) => setBrandData({ ...brandData, is_active: e.target.checked })}
                    className="w-4 h-4 text-primary border-gray-300 dark:border-gray-600 rounded focus:ring-primary"
                  />
                  <label htmlFor="brand_is_active" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('computer.fieldActive')}
                  </label>
                </div>
              </div>

              <div className="mt-4 md:mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
                <button
                  onClick={() => { setShowBrandModal(false); resetBrandForm(); }}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSaveBrand}
                  className="w-full sm:w-auto px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  {editingBrand ? t('computer.update') : t('common.save')}
                </button>
              </div>

              {/* Brands List */}
              <div className="mt-6 border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">{t('computer.existingBrands')}</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {brands.map(brand => (
                    <div key={brand.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-3">
                        {brand.logo_url && (
                          <img src={`${API_BASE_URL.replace('/api', '')}${brand.logo_url}`} alt={brand.name} className="w-10 h-10 object-contain" />
                        )}
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">{brand.name}</div>
                          {brand.description && <div className="text-sm text-gray-500 dark:text-gray-400">{brand.description}</div>}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditBrand(brand)}
                          className="text-primary hover:text-primary-dark"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteBrand(brand.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Modal>

      {/* Assign Modal */}
      <Modal isOpen={showAssignModal} onClose={() => setShowAssignModal(false)}>
          {selectedComputer && (<div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-4 md:p-6 max-h-[85vh] overflow-y-auto">
            <h3 className="text-base md:text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">{t('computer.assignTitle')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {selectedComputer.inventory_number} - {selectedComputer.brand} {selectedComputer.model}
            </p>

            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={t('computer.userSearchPlaceholder')}
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            <select
              value={selectedUserId || ''}
              onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-4"
              size={10}
            >
              <option value="">{t('computer.selectUser')}</option>
              {users
                .filter(user => 
                  userSearchTerm === '' ||
                  user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                  user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
                )
                .map(user => (
                  <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                ))}
            </select>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
              <button
                onClick={() => { setShowAssignModal(false); setSelectedComputer(null); setSelectedUserId(null); setUserSearchTerm(''); }}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  const user = users.find(u => u.id === selectedUserId);
                  if (user && selectedComputer && selectedUserId) {
                    handleAssignmentWithForm(selectedComputer, selectedUserId.toString(), user.name);
                  }
                }}
                disabled={!selectedUserId}
                className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
              >
                <FileText className="w-4 h-4" />
                {t('computer.assignWithForm')}
              </button>
            </div>
          </div>)}
        </Modal>

      {/* Zimmet Formu Onay Modal */}
      <Modal isOpen={showAssignmentFormModal} onClose={() => setShowAssignmentFormModal(false)}>
          {pendingAssignment && (<div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[85vh] overflow-y-auto">
            <div className="p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('computer.assignFormTitle')}
              </h3>
            </div>

            <div className="p-4 md:p-6">
              <div className="mb-4 p-4 bg-primary/10 dark:bg-primary/20 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>{t('computer.labelComputer')}</strong> {pendingAssignment.computer?.inventory_number} - {pendingAssignment.computer?.brand} {pendingAssignment.computer?.model}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                  <strong>{t('computer.labelUser')}</strong> {pendingAssignment.userName}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('computer.formNotesOptional')}
                </label>
                <textarea
                  value={assignmentFormNotes}
                  onChange={(e) => setAssignmentFormNotes(e.target.value)}
                  rows={3}
                  placeholder={t('computer.assignFormNotesPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            <div className="p-4 md:p-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col space-y-2 md:space-y-3">
                {/* Ana Aksiyon Butonları */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={confirmAssignmentWithForm}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="text-sm md:text-base">{t('computer.assignWithForm')}</span>
                  </button>
                  <button
                    onClick={confirmAssignmentWithoutForm}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    <span className="text-sm md:text-base">{t('computer.assignWithoutForm')}</span>
                  </button>
                </div>

                {/* İptal Butonu */}
                <button
                  onClick={() => {
                    setShowAssignmentFormModal(false);
                    setPendingAssignment(null);
                    setAssignmentFormNotes('');
                    setShowAssignModal(true);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors text-sm md:text-base"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>)}
        </Modal>

      {/* Atama Kaldırma (Geri Zimmet) Modal */}
      <Modal isOpen={showUnassignModal} onClose={() => setShowUnassignModal(false)}>
          {pendingUnassignment && (<div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[85vh] overflow-y-auto">
            <div className="p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('computer.unassignFormTitle')}
              </h3>
            </div>

            <div className="p-4 md:p-6">
              <div className="mb-4 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>{t('computer.labelComputer')}</strong> {pendingUnassignment.inventory_number} - {pendingUnassignment.brand} {pendingUnassignment.model}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                  <strong>{t('computer.labelUser')}</strong> {pendingUnassignment.assigned_user_name}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('computer.formNotesOptional')}
                </label>
                <textarea
                  value={unassignmentFormNotes}
                  onChange={(e) => setUnassignmentFormNotes(e.target.value)}
                  rows={3}
                  placeholder={t('computer.unassignFormNotesPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              {/* Cihaz Fotoğrafları */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('computer.devicePhotos')}
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {unassignmentPhotos.map((photo, idx) => (
                    <div key={idx} className="relative aspect-square">
                      <img src={photo} alt={`Fotoğraf ${idx + 1}`} className="w-full h-full object-cover rounded-md border border-gray-300 dark:border-gray-600" />
                      <button
                        onClick={() => setUnassignmentPhotos(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {unassignmentPhotos.length < 4 && (
                    <label className="aspect-square border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors">
                      <Camera className="w-5 h-5 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-400">Ekle</span>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        className="hidden"
                        onChange={addUnassignmentPhoto}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div className="p-4 md:p-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col space-y-2 md:space-y-3">
                {/* Ana Aksiyon Butonları */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={confirmUnassignmentWithForm}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="text-sm md:text-base">{t('computer.unassignWithForm')}</span>
                  </button>
                  <button
                    onClick={confirmUnassignmentWithoutForm}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
                  >
                    <UserMinus className="w-4 h-4" />
                    <span className="text-sm md:text-base">{t('computer.unassignWithoutForm')}</span>
                  </button>
                </div>

                {/* İptal Butonu */}
                <button
                  onClick={() => {
                    setShowUnassignModal(false);
                    setPendingUnassignment(null);
                    setUnassignmentFormNotes('');
                    setUnassignmentPhotos([]);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors text-sm md:text-base"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>)}
        </Modal>

      {/* Scrap Computer Modal */}
      <Modal isOpen={showScrapModal} onClose={() => setShowScrapModal(false)}>
          {pendingScrapComputer && (<div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-stone-600 to-stone-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Trash2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{t('computer.scrapFormTitle')}</h2>
                  <p className="text-stone-200 text-sm">
                    {pendingScrapComputer.inventory_number} - {pendingScrapComputer.brand} {pendingScrapComputer.model}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowScrapModal(false);
                  setPendingScrapComputer(null);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Step Indicator */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 dark:bg-gray-900 border-b dark:border-gray-700">
              <div className="flex items-center justify-between">
                {[t('computer.scrapStep1'), t('computer.scrapStep2'), t('computer.scrapStep3'), t('computer.scrapStep4'), t('computer.scrapStep5')].map((step, index) => (
                  <div key={index} className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      scrapFormStep > index + 1 ? 'bg-green-500 text-white' :
                      scrapFormStep === index + 1 ? 'bg-stone-600 text-white' :
                      'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                    }`}>
                      {scrapFormStep > index + 1 ? <CheckCircle className="w-5 h-5" /> : index + 1}
                    </div>
                    <span className={`ml-2 text-sm hidden md:inline ${
                      scrapFormStep === index + 1 ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                    }`}>{step}</span>
                    {index < 4 && <div className="w-8 md:w-16 h-1 mx-2 bg-gray-300 dark:bg-gray-600 rounded" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Step 1: Cihaz Bilgileri */}
              {scrapFormStep === 1 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('computer.scrapStep1Title')}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.scrapFieldDeviceType')}</label>
                      <select
                        value={scrapFormData.device_type}
                        onChange={(e) => setScrapFormData({ ...scrapFormData, device_type: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="laptop">Laptop</option>
                        <option value="desktop">Desktop</option>
                        <option value="workstation">Workstation</option>
                        <option value="server">Server</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.colInventoryNo')}</label>
                      <input type="text" value={pendingScrapComputer.inventory_number} disabled className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldBrand').replace(' *', '')}</label>
                      <input type="text" value={pendingScrapComputer.brand} disabled className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldModel').replace(' *', '')}</label>
                      <input type="text" value={pendingScrapComputer.model} disabled className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.colSerialNo')}</label>
                      <input type="text" value={pendingScrapComputer.serial_number} disabled className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldPurchaseDate')}</label>
                      <input type="text" value={pendingScrapComputer.purchase_date ? new Date(pendingScrapComputer.purchase_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR') : '-'} disabled className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.fieldLocation')}</label>
                      <input type="text" value={pendingScrapComputer.location_name || '-'} disabled className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.scrapFieldPreviousUser')}</label>
                      <input type="text" value={pendingScrapComputer.assigned_user_name || '-'} disabled className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300" />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Teknik Donanım Durumu */}
              {scrapFormStep === 2 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('computer.scrapStep2Title')}</h3>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700 dark:bg-gray-900">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('computer.scrapColPart')}</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('computer.scrapColStatus')}</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('computer.scrapColDetail')}</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('computer.scrapColSparePart')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {[
                          { key: 'cpu', label: t('computer.scrapPartCPU'), options: ['exists', 'missing'] },
                          { key: 'ram', label: t('computer.scrapPartRAM'), options: ['exists', 'missing'] },
                          { key: 'storage', label: t('computer.scrapPartStorage'), options: ['exists', 'missing', 'removed'] },
                          { key: 'screen', label: t('computer.scrapPartScreen'), options: ['good', 'broken', 'dead_pixel', 'stain', 'na'] },
                          { key: 'motherboard', label: t('computer.scrapPartMotherboard'), options: ['working', 'faulty', 'short_circuit'] },
                          { key: 'battery', label: t('computer.scrapPartBattery'), options: ['exists', 'missing', 'swollen', 'not_charging', 'na'] },
                          { key: 'adapter', label: t('computer.scrapPartAdapter'), options: ['exists', 'missing'] },
                          { key: 'case_condition', label: t('computer.scrapPartCase'), options: ['good', 'broken', 'incomplete'] },
                          { key: 'keyboard', label: t('computer.scrapPartKeyboard'), options: ['working', 'faulty', 'na'] },
                          { key: 'ports', label: t('computer.scrapPartPorts'), options: ['good', 'oxidized', 'faulty'] }
                        ].map((item) => (
                          <tr key={item.key} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                            <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">{item.label}</td>
                            <td className="px-4 py-2">
                              <select
                                value={scrapFormData.hardware_checklist[item.key as keyof typeof scrapFormData.hardware_checklist].status}
                                onChange={(e) => setScrapFormData({
                                  ...scrapFormData,
                                  hardware_checklist: {
                                    ...scrapFormData.hardware_checklist,
                                    [item.key]: { ...scrapFormData.hardware_checklist[item.key as keyof typeof scrapFormData.hardware_checklist], status: e.target.value }
                                  }
                                })}
                                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              >
                                {item.options.map(opt => (
                                  <option key={opt} value={opt}>
                                    {opt === 'exists' ? t('computer.scrapOptExists') : opt === 'missing' ? t('computer.scrapOptMissing') : opt === 'removed' ? t('computer.scrapOptRemoved') :
                                     opt === 'good' ? t('computer.scrapOptGood') : opt === 'broken' ? t('computer.scrapOptBroken') : opt === 'dead_pixel' ? t('computer.scrapOptDeadPixel') :
                                     opt === 'stain' ? t('computer.scrapOptStain') : opt === 'na' ? t('computer.scrapOptNA') : opt === 'working' ? t('computer.scrapOptWorking') :
                                     opt === 'faulty' ? t('computer.scrapOptFaulty') : opt === 'short_circuit' ? t('computer.scrapOptShortCircuit') : opt === 'swollen' ? t('computer.scrapOptSwollen') :
                                     opt === 'not_charging' ? t('computer.scrapOptNotCharging') : opt === 'incomplete' ? t('computer.scrapOptIncomplete') : opt === 'oxidized' ? t('computer.scrapOptOxidized') : opt}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={scrapFormData.hardware_checklist[item.key as keyof typeof scrapFormData.hardware_checklist].detail}
                                onChange={(e) => setScrapFormData({
                                  ...scrapFormData,
                                  hardware_checklist: {
                                    ...scrapFormData.hardware_checklist,
                                    [item.key]: { ...scrapFormData.hardware_checklist[item.key as keyof typeof scrapFormData.hardware_checklist], detail: e.target.value }
                                  }
                                })}
                                placeholder="Detay..."
                                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              />
                            </td>
                            <td className="px-4 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={scrapFormData.hardware_checklist[item.key as keyof typeof scrapFormData.hardware_checklist].spare_part}
                                onChange={(e) => setScrapFormData({
                                  ...scrapFormData,
                                  hardware_checklist: {
                                    ...scrapFormData.hardware_checklist,
                                    [item.key]: { ...scrapFormData.hardware_checklist[item.key as keyof typeof scrapFormData.hardware_checklist], spare_part: e.target.checked }
                                  }
                                })}
                                className="w-4 h-4 text-stone-600 rounded"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Step 3: Hurdaya Ayırma Nedeni */}
              {scrapFormStep === 3 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('computer.scrapStep3Title')}</h3>

                  <div className="space-y-3">
                    {[
                      { value: 'physical_damage', label: t('computer.scrapReasonPhysical') },
                      { value: 'obsolete', label: t('computer.scrapReasonObsolete') },
                      { value: 'repair_cost', label: t('computer.scrapReasonRepairCost') },
                      { value: 'parts_unavailable', label: t('computer.scrapReasonParts') },
                      { value: 'other', label: t('computer.scrapReasonOther') }
                    ].map((reason) => (
                      <label key={reason.value} className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-600">
                        <input
                          type="checkbox"
                          checked={(scrapFormData.scrap_reasons || []).includes(reason.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setScrapFormData({ ...scrapFormData, scrap_reasons: [...scrapFormData.scrap_reasons, reason.value] });
                            } else {
                              setScrapFormData({ ...scrapFormData, scrap_reasons: (scrapFormData.scrap_reasons || []).filter(r => r !== reason.value) });
                            }
                          }}
                          className="w-5 h-5 text-stone-600 rounded mr-3"
                        />
                        <span className="text-gray-900 dark:text-gray-100">{reason.label}</span>
                      </label>
                    ))}
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.scrapReasonDetailLabel')}</label>
                    <textarea
                      value={scrapFormData.scrap_reason_detail}
                      onChange={(e) => setScrapFormData({ ...scrapFormData, scrap_reason_detail: e.target.value })}
                      rows={4}
                      placeholder={t('computer.scrapReasonDetailPlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Veri Güvenliği */}
              {scrapFormStep === 4 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('computer.scrapStep4Title')}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={scrapFormData.disk_removed}
                          onChange={(e) => setScrapFormData({ ...scrapFormData, disk_removed: e.target.checked })}
                          className="w-5 h-5 text-stone-600 rounded mr-3"
                        />
                        <span className="text-gray-900 dark:text-gray-100">{t('computer.scrapDiskRemoved')}</span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.scrapDataWipeMethod')}</label>
                      <select
                        value={scrapFormData.data_wipe_method}
                        onChange={(e) => setScrapFormData({ ...scrapFormData, data_wipe_method: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="na">{t('computer.scrapWipeNA')}</option>
                        <option value="software_wipe">{t('computer.scrapWipeSoftware')}</option>
                        <option value="physical_drill">{t('computer.scrapWipePhysical')}</option>
                        <option value="degauss">{t('computer.scrapWipeDegauss')}</option>
                        <option value="not_wiped">{t('computer.scrapWipeNot')}</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={scrapFormData.media_check_done}
                          onChange={(e) => setScrapFormData({ ...scrapFormData, media_check_done: e.target.checked })}
                          className="w-5 h-5 text-stone-600 rounded mr-3"
                        />
                        <span className="text-gray-900 dark:text-gray-100">{t('computer.scrapMediaCheck')}</span>
                      </label>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('computer.scrapDataSecurityNote')}</label>
                      <textarea
                        value={scrapFormData.data_security_notes}
                        onChange={(e) => setScrapFormData({ ...scrapFormData, data_security_notes: e.target.value })}
                        rows={3}
                        placeholder={t('computer.scrapDataSecurityPlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Fotoğraflar */}
              {scrapFormStep === 5 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('computer.scrapStep5Title')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{t('computer.scrapPhotosHint')}</p>

                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleScrapPhotoAdd}
                      className="hidden"
                      id="scrap-photo-input"
                      disabled={(scrapFormData.photos || []).length >= 5}
                    />
                    <label
                      htmlFor="scrap-photo-input"
                      className={`cursor-pointer flex flex-col items-center ${(scrapFormData.photos || []).length >= 5 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Upload className="w-12 h-12 text-gray-400 mb-2" />
                      <span className="text-gray-600 dark:text-gray-400">{t('computer.scrapUploadClick')}</span>
                      <span className="text-sm text-gray-400 mt-1">{(scrapFormData.photos || []).length}/5 {t('computer.scrapStep5')}</span>
                    </label>
                  </div>

                  {(scrapFormData.photos || []).length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
                      {(scrapFormData.photos || []).map((photo, index) => (
                        <div key={index} className="relative">
                          <img
                            src={URL.createObjectURL(photo)}
                            alt={`Fotoğraf ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => handleScrapPhotoRemove(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 dark:bg-gray-900 border-t dark:border-gray-700 flex justify-between">
              <button
                onClick={() => scrapFormStep > 1 && setScrapFormStep(scrapFormStep - 1)}
                disabled={scrapFormStep === 1}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.back')}
              </button>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowScrapModal(false);
                    setPendingScrapComputer(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>

                {scrapFormStep < 5 ? (
                  <button
                    onClick={() => setScrapFormStep(scrapFormStep + 1)}
                    className="px-4 py-2 bg-stone-600 text-white rounded-md hover:bg-stone-700"
                  >
                    {t('common.next')}
                  </button>
                ) : (
                  <button
                    onClick={handleSubmitScrapForm}
                    disabled={scrapFormLoading || (scrapFormData.scrap_reasons || []).length === 0}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {scrapFormLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        {t('computer.scrapProcessing')}
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        {t('computer.scrapSubmit')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>)}
        </Modal>

      {/* History Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          {selectedComputerForHistory && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <History className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{t('computer.historyTitle')}</h2>
                  {selectedComputerForHistory && (
                    <p className="text-purple-200 text-sm">
                      {selectedComputerForHistory.inventory_number} - {selectedComputerForHistory.brand} {selectedComputerForHistory.model}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setComputerHistory([]);
                  setSelectedComputerForHistory(null);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-100px)] p-6">
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : computerHistory.length > 0 ? (
                <div className="space-y-4">
                  {computerHistory.map((history, index) => {
                    const actionStyle = getActionStyle(history.action_type);
                    return (
                      <div
                        key={history.id}
                        className={`relative pl-8 pb-4 ${index !== computerHistory.length - 1 ? 'border-l-2 border-gray-200 dark:border-gray-700 ml-2' : 'ml-2'}`}
                      >
                        <div className={`absolute left-[-10px] top-0 w-6 h-6 rounded-full ${actionStyle.bg} flex items-center justify-center`}>
                          <span className={actionStyle.color}>{actionStyle.icon}</span>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${actionStyle.bg} ${actionStyle.color}`}>
                              {actionStyle.icon}
                              {actionStyle.label}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(history.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                            </span>
                          </div>

                          <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                            {history.notes && (
                              <p className="font-medium">{history.notes}</p>
                            )}
                            <p className="text-gray-500 dark:text-gray-400">
                              <span className="font-medium">{t('computer.historyPerformedBy')}</span> {history.performed_by_user_name || (language === 'en' ? 'System' : 'Sistem')}
                            </p>
                            {history.assigned_user_name && (
                              <p className="text-gray-500 dark:text-gray-400">
                                <span className="font-medium">{t('computer.historyAssignedUser')}</span> {history.assigned_user_name}
                              </p>
                            )}
                            {history.old_values && history.action_type === 'update' && (
                              <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 dark:border-gray-600">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('computer.historyChanges')}</p>
                                <div className="text-xs">
                                  {(() => {
                                    try {
                                      const oldVals = JSON.parse(history.old_values || '{}') || {};
                                      const newVals = JSON.parse(history.new_values || '{}') || {};
                                      return Object.keys(newVals).map(key => (
                                        oldVals[key] !== newVals[key] && (
                                          <p key={key}>
                                            <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                                            <span className="text-red-500 line-through">{oldVals[key] || '-'}</span>
                                            {' → '}
                                            <span className="text-green-500">{newVals[key] || '-'}</span>
                                          </p>
                                        )
                                      ));
                                    } catch {
                                      return null;
                                    }
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('computer.historyNoData')}</p>
                </div>
              )}
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default ComputersPage;
